import { useState, useEffect, useRef } from 'react';
import { reverseGeocode, getCachedAddress } from '../utils/reverseGeocode';

/**
 * Hook to reverse geocode a single lat/lon pair.
 * Returns { address, loading }.
 * Lat/lon is shown on hover via the AddressDisplay component.
 */
export function useReverseGeocode(lat, lon) {
  const [address, setAddress] = useState(() => getCachedAddress(lat, lon));
  const [loading, setLoading] = useState(!getCachedAddress(lat, lon) && lat != null && lon != null);

  useEffect(() => {
    if (lat == null || lon == null) {
      setAddress(null);
      setLoading(false);
      return;
    }

    const cached = getCachedAddress(lat, lon);
    if (cached) {
      setAddress(cached);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    reverseGeocode(lat, lon).then((addr) => {
      if (!cancelled) {
        setAddress(addr);
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [lat, lon]);

  return { address, loading };
}

/**
 * Hook to resolve addresses for an array of coordinate objects.
 * Each item should have { lat, lon } (or any keys - pass a mapper).
 * Returns { addressMap, loading } where addressMap is keyed by "lat,lon".
 */
export function useAddressMap(coordinates) {
  const [addressMap, setAddressMap] = useState({});
  const [loading, setLoading] = useState(false);
  const coordRef = useRef('');

  useEffect(() => {
    if (!coordinates || coordinates.length === 0) {
      setAddressMap({});
      setLoading(false);
      return;
    }

    // Serialize to detect actual changes
    const serialized = coordinates.map((c) => `${c.lat},${c.lon}`).join('|');
    if (serialized === coordRef.current) return;
    coordRef.current = serialized;

    let cancelled = false;
    setLoading(true);

    const resolveAll = async () => {
      const map = {};
      for (const { lat, lon } of coordinates) {
        if (cancelled) return;
        const addr = await reverseGeocode(lat, lon);
        const key = `${Number(lat).toFixed(5)},${Number(lon).toFixed(5)}`;
        map[key] = addr;
        // Update progressively so UI shows addresses as they resolve
        if (!cancelled) setAddressMap({ ...map });
      }
      if (!cancelled) setLoading(false);
    };

    resolveAll();
    return () => {
      cancelled = true;
    };
  }, [coordinates]);

  return { addressMap, loading };
}
